/**
 * Lightweight hook for basic friends list (id, name, username, avatar only).
 * Used by chat modals and other places that don't need portfolio data.
 * Much faster than useFriends since it skips portfolio calculations.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../auth/hooks/useAuth';
import { fetchFriendsBasic } from '../services/friendsApi';

export type BasicFriend = {
  friendId: string;
  name: string;
  username: string | null;
  profilePictureUrl: string | null;
};

export function useFriendsBasic(enabled: boolean = true) {
  const { session, sessionEstablishing } = useAuth();
  
  const { 
    data: friends = [], 
    isLoading,
    error
  } = useQuery<BasicFriend[]>({
    queryKey: ['friends', 'basic'],
    queryFn: () => fetchFriendsBasic(session!.access_token),
    enabled: enabled && !!session?.access_token && !sessionEstablishing,
    staleTime: 300000, // 5 minutes (longer since no portfolio data to invalidate)
    gcTime: 600000, // 10 minutes (keep in cache longer)
    retry: 2,
    // SWR: Keep previous data while refetching
    placeholderData: (previousData) => previousData,
  });

  return {
    friends,
    loading: isLoading,
    error: error ? 'Failed to load friends' : null
  };
}
